import { useRef, useState } from "react";
import axios from "axios";
import QRCode from "qrcode";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Download, FileDown, Loader2 } from "lucide-react";
import { passQrPayload } from "@/lib/eventLib";
import { PassCard } from "@/components/events/PassCard";
import { ContactLinks } from "@/components/events/ContactNumbers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PassDownloadDialog({ open, onClose, token, ev }) {
  const [fieldVal, setFieldVal] = useState("");
  const [passKey, setPassKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [pass, setPass] = useState(null);
  const [qr, setQr] = useState("");
  const passRef = useRef(null);

  const lookupLabel = (ev.fields || []).find((f) => f.key === ev.pass_key_field)?.label || "Your detail";

  const reset = () => { setFieldVal(""); setPassKey(""); setPass(null); setQr(""); };
  const close = () => { reset(); onClose(); };

  const find = async () => {
    if (!fieldVal.trim() || !passKey.trim()) return toast.error("Dono fields bharein");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/public/event/${token}/pass-lookup`, { field_value: fieldVal, pass_key: passKey });
      const pt = r.data.pass_token;
      const [pr, q] = await Promise.all([
        axios.get(`${API}/public/pass/${pt}`),
        QRCode.toDataURL(passQrPayload(pt), { width: 600, margin: 1 }),
      ]);
      setQr(q);
      setPass(pr.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Match nahi hua");
    } finally { setBusy(false); }
  };

  const download = async (type) => {
    if (!passRef.current) return;
    try {
      // Candidate photo jaise images ka load poora hone ka wait — warna download me photo miss hoti hai
      const imgs = Array.from(passRef.current.querySelectorAll("img"));
      await Promise.all(imgs.map((im) => (im.complete && im.naturalWidth ? Promise.resolve() : new Promise((r) => { im.onload = r; im.onerror = r; }))));
      const canvas = await html2canvas(passRef.current, { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false, imageTimeout: 20000 });
      const img = canvas.toDataURL("image/jpeg", 0.92);
      const base = `${(pass.candidate_name || "pass").replace(/[^a-z0-9]+/gi, "-")}`;
      if (type === "jpg") {
        const a = document.createElement("a"); a.href = img; a.download = `${base}.jpg`; a.click();
      } else {
        const pdf = new jsPDF({ unit: "px", format: [canvas.width, canvas.height], orientation: canvas.width >= canvas.height ? "landscape" : "portrait" });
        pdf.addImage(img, "JPEG", 0, 0, canvas.width, canvas.height);
        pdf.save(`${base}.pdf`);
      }
      toast.success("Pass download ho gaya");
    } catch { toast.error("Download fail hua"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-md" data-testid="pass-download-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-indigo-600" /> Download Your Pass</DialogTitle></DialogHeader>

        {!pass ? (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{lookupLabel}</Label>
              <Input value={fieldVal} onChange={(e) => setFieldVal(e.target.value)} placeholder={`Enter your ${lookupLabel}`} data-testid="passdl-field-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Pass Key</Label>
              <Input value={passKey} onChange={(e) => setPassKey(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="4 ya 6 digit key" data-testid="passdl-key-input" />
            </div>
            <Button onClick={find} disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" data-testid="passdl-find-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} {busy ? "Dhoondh rahe..." : "Pass dikhayein"}
            </Button>
            {ev.contact_number || ev.organiser_mobile ? (
              <p className="text-xs text-center text-muted-foreground pt-1" data-testid="passdl-forgot">
                Pass key bhool gaye? Organiser se sampark karein:{" "}
                <ContactLinks value={ev.contact_number || ev.organiser_mobile} iconClass="h-3 w-3" className="text-indigo-700 font-semibold justify-center" testId="passdl-contact" />
              </p>
            ) : (
              <p className="text-xs text-center text-muted-foreground pt-1" data-testid="passdl-forgot">Pass key bhool gaye? Event organiser se sampark karein.</p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {pass.status !== "approved" && <p className="text-xs text-center text-amber-600 bg-amber-50 border border-amber-200 rounded-lg py-1.5" data-testid="passdl-pending">Aapka pass abhi approve nahi hua — approve hone par download kar paayenge.</p>}
            <div className="max-h-[52vh] overflow-y-auto rounded-lg">
              <PassCard ref={passRef} pass={pass} qr={qr} widthPx={400} />
            </div>
            {pass.status === "approved" && (
              <div className="flex gap-2">
                <Button onClick={() => download("jpg")} variant="outline" className="flex-1 gap-1.5" data-testid="passdl-jpg-btn"><Download className="h-4 w-4" /> JPG</Button>
                <Button onClick={() => download("pdf")} className="flex-1 gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="passdl-pdf-btn"><FileDown className="h-4 w-4" /> PDF</Button>
              </div>
            )}
            <Button variant="ghost" onClick={reset} className="w-full text-xs" data-testid="passdl-back-btn">Doosra pass dhoondhein</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
